import { DevTool } from '@hookform/devtools';
import { yupResolver } from '@hookform/resolvers/yup';
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	FormGroup,
	Grid,
	Stack,
	Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import React from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { usePayment, useUpdatePaymentMutation } from '../../../api/PaymentsApi';
import {
	MULTI_CURRENCY_FEATURE_ENABLE,
	UI_CASH_MANAGEMENT_FEATURE_ENABLED,
	UI_MULTI_CURRENCY_FEATURE_ENABLE,
	useAuthContext,
} from '../../../context/auth';
import { useConfigContext } from '../../../context/config';
import { usePolicyContext } from '../../../context/policy';
import currencyCodes from '../../../data/currencyCodes';
import { CurrencyCode } from '../../../types/currencyCode';
import {
	Payment,
	PaymentActionRequest,
	PaymentUpdateRequest,
} from '../../../types/payments';
import Error from '../../Error';
import { FastForm } from '../../fastForm';
import {
	FORM_NAME,
	PAYMENT_SPECIAL_FIELDS,
} from '../../fastForm/data/constants';
import {
	CurrencyField,
	PolicyholderLookupField,
	PolicyLookupField,
	SelectField,
} from '../../fastForm/fields';
import {
	getDefaultPolicyValues,
	getPaymentForm,
} from '../../fastForm/formBuilders';
import { PaymentFormValues } from '../../fastForm/formBuilders/getPaymentForm';
import paymentUpdateBuilder from '../../fastForm/updateBuilders/paymentUpdateBuilder';
import { getValidationSchemaWithoutResolver } from '../../fastForm/utils/getValidationSchema';
import LargeCenterSpinner from '../../LargeCenterSpinner';
import { successToast } from '../../Toast';
import ActiveLastBreadcrumb from '../components/Breadcrumbs';
import PaymentStateChip from '../components/PaymentStateChip';
import {
	EDIT_PAYMENT_PAGE__CANCEL,
	EDIT_PAYMENT_PAGE__PAYMENT_NOT_FOUND,
	EDIT_PAYMENT_PAGE__PAYMENT_NOT_FOUND_ACTION_TEXT,
	EDIT_PAYMENT_PAGE__READY_TO_POST_LABEL,
	EDIT_PAYMENT_PAGE__SUBMIT,
	EDIT_PAYMENT_PAGE__TITLE,
	PAYMENT_API_MUTATION__UPDATE_SUCCESS_TOAST,
} from '../data/constants';
import { paymentSchema } from '../lib/paymentSchema';

interface EditPaymentPageProps {
	payment: Payment;
	disableBreadcrumb?: boolean;
}

const EditPaymentPage = (props: EditPaymentPageProps) => {
	const {
		policyholderId: policyholderLocatorFromParams,
		policyId: policyLocatorFromParams,
	} = useParams();
	const { payment, disableBreadcrumb } = props;
	const authData = useAuthContext();
	const config = useConfigContext();
	const policyData = usePolicyContext();
	const queryClient = useQueryClient();
	const { paymentLocator } = useParams();
	const [readyToPost, setReadyToPost] = React.useState(false);
	const multiCurrencyEnabled =
		authData?.auth?.configProperties?.[MULTI_CURRENCY_FEATURE_ENABLE] &&
		authData?.auth?.configProperties?.[UI_MULTI_CURRENCY_FEATURE_ENABLE];
	const navigate = useNavigate();
	const timezone = authData.auth?.timezone;
	const authToken = authData.auth?.authorizationToken;
	const paymentFields = config.paymentConfiguration.paymentConfiguration.fields;

	const form = getPaymentForm({
		payment: payment,
		fields: paymentFields,
		fieldValues: payment?.fieldValues || {},
		mediaByLocator: {},
		timezone,
		multiCurrencyEnabled,
	});

	const paymentFormValues = getDefaultPolicyValues(form, authData);

	const specialFieldValues = {
		amountCurrency: payment?.amountCurrency || '',
		amount: payment?.amount || '',
		policyholderLocator: payment?.policyholderLocator || '',
		policyLocator: payment?.policyLocator || '',
	};

	const defaultValues = {
		payment: {
			...(paymentFormValues.payment as object),
			...specialFieldValues,
		},
	} as PaymentFormValues;

	const methods = useForm<PaymentFormValues>({
		defaultValues,
		mode: 'onBlur',
		reValidateMode: 'onChange',
		shouldFocusError: true,
		resolver:
			form &&
			yupResolver(
				getValidationSchemaWithoutResolver(form, !readyToPost).concat(
					paymentSchema(readyToPost),
				),
			),
	});

	const { formState, control, handleSubmit, watch } = methods;
	const dirtyFields = formState.dirtyFields;

	const updatePayment = useUpdatePaymentMutation({
		paymentLocator,
		authToken,
		queryClient,
	});

	const currency = watch(
		`${FORM_NAME.payment as 'payment'}.${
			PAYMENT_SPECIAL_FIELDS.amountCurrency
		}`,
	) as CurrencyCode;

	const onSubmit: SubmitHandler<PaymentFormValues> = async (formValues) => {
		const payment: PaymentUpdateRequest = paymentUpdateBuilder(
			dirtyFields,
			formValues,
			form,
			FORM_NAME.payment,
			timezone,
			currency,
		);

		const request: PaymentActionRequest = {
			paymentUpdate: payment,
		};

		// Gotta do this first in case the user makes change to the payment
		await updatePayment.mutateAsync(request);

		if (readyToPost) {
			updatePayment.mutate({ action: 'ready' });
		}
		successToast(PAYMENT_API_MUTATION__UPDATE_SUCCESS_TOAST);
		navigate(`../`);
	};

	const handleReadyToPost = (event: React.ChangeEvent<HTMLInputElement>) => {
		setReadyToPost(event.target.checked);
	};

	if (!payment) {
		return <LargeCenterSpinner />;
	}

	const currencyCodeOptions = multiCurrencyEnabled
		? currencyCodes.map(({ value }) => value)
		: ['USD'];

	return (
		<FormProvider {...methods}>
			<Box component='form' onSubmit={handleSubmit(onSubmit)} noValidate>
				<Stack spacing={2} alignItems='flex-start'>
					{!disableBreadcrumb && <ActiveLastBreadcrumb isEdit />}
					<Stack spacing={2} direction='row' alignItems='center' pb={4}>
						<Typography variant='h3'>{EDIT_PAYMENT_PAGE__TITLE}</Typography>
						<PaymentStateChip state={payment.state} />
					</Stack>
				</Stack>
				<Grid container spacing={2}>
					<Grid item xs={6}>
						<SelectField<PaymentFormValues>
							order={0}
							name={`${FORM_NAME.payment as 'payment'}.${
								PAYMENT_SPECIAL_FIELDS.amountCurrency
							}`}
							title='Currency'
							disabled={updatePayment.isLoading}
							control={control}
							type='select'
							values={currencyCodeOptions}
						/>
					</Grid>
					<Grid item xs={6}>
						<CurrencyField
							order={1}
							name={`${FORM_NAME.payment as 'payment'}.${
								PAYMENT_SPECIAL_FIELDS.amount
							}`}
							currency={currency}
							title='Amount'
							disabled={updatePayment.isLoading}
							control={control}
							type='select'
							values={currencyCodeOptions}
						/>
					</Grid>
					<Grid item xs={6}>
						<PolicyholderLookupField<PaymentFormValues>
							order={2}
							name={`${FORM_NAME.payment as 'payment'}.${
								PAYMENT_SPECIAL_FIELDS.policyholderLocator
							}`}
							title='Policyholder'
							disabled={
								updatePayment.isLoading ||
								!!policyholderLocatorFromParams ||
								!!policyLocatorFromParams
							}
							control={control}
							type='text'
							values={currencyCodeOptions}
						/>
					</Grid>
					<Grid item xs={6}>
						<PolicyLookupField<PaymentFormValues>
							order={3}
							name={`${FORM_NAME.payment as 'payment'}.${
								PAYMENT_SPECIAL_FIELDS.policyLocator
							}`}
							title='Policy'
							disabled={updatePayment.isLoading || !!policyLocatorFromParams}
							control={control}
							type='text'
							values={currencyCodeOptions}
						/>
					</Grid>
					<FastForm
						formName={FORM_NAME.payment}
						form={form}
						configVersion=''
						productName={policyData?.productConfig?.name}
						authData={authData}
						disabled={updatePayment.isLoading}
					/>
				</Grid>
				<Stack
					justifyContent='flex-end'
					direction='row'
					alignItems='center'
					sx={{ mt: 2 }}
				>
					<FormGroup>
						<FormControlLabel
							label={EDIT_PAYMENT_PAGE__READY_TO_POST_LABEL}
							control={<Checkbox />}
							onChange={handleReadyToPost}
							checked={readyToPost}
							color='secondary'
						/>
					</FormGroup>
				</Stack>
				<Stack
					spacing={2}
					direction='row'
					alignItems='center'
					justifyContent='flex-end'
					pt={4}
				>
					<Button type='button' onClick={() => navigate(-1)}>
						{EDIT_PAYMENT_PAGE__CANCEL}
					</Button>
					<Button variant='contained' type='submit'>
						{EDIT_PAYMENT_PAGE__SUBMIT}
					</Button>
				</Stack>
				<DevTool control={control} />
			</Box>
		</FormProvider>
	);
};

const EditPaymentPageWrapper = () => {
	const { paymentLocator, policyholderId, policyId } = useParams();
	const navigate = useNavigate();
	const authData = useAuthContext();
	const authToken = authData.auth?.authorizationToken;
	const cashManagementEnabled =
		authData?.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED];

	const {
		data: payment,
		isLoading,
		isError,
		error,
	} = usePayment(
		paymentLocator,
		authToken,
		cashManagementEnabled,
		authData?.hasPermission('payment.fetch'),
	);

	if (isError && axios.isAxiosError(error)) {
		return (
			<Error
				error={
					error?.response?.data.message ||
					`${EDIT_PAYMENT_PAGE__PAYMENT_NOT_FOUND} ${paymentLocator}`
				}
				primaryAction={() => navigate('/payments')}
				primaryActionText={EDIT_PAYMENT_PAGE__PAYMENT_NOT_FOUND_ACTION_TEXT}
			/>
		);
	}

	if (isLoading || !payment) return <LargeCenterSpinner />;

	return (
		<EditPaymentPage
			payment={payment}
			disableBreadcrumb={!!policyholderId || !!policyId}
		/>
	);
};

export default EditPaymentPageWrapper;
